import { useState, useEffect, useCallback, useRef } from 'react';

// ── Constants (mirror backend engine.py) ───────────────────────────

const SUMMARY_FIELDS = [
  { value: 'dar_submitted', label: 'DAR Submitted', type: 'boolean' as const },
  { value: 'has_detailed_notes', label: 'Has Detailed Notes', type: 'boolean' as const },
  { value: 'total_patrols', label: 'Total Patrols', type: 'number' as const },
  { value: 'total_incidents', label: 'Total Incidents', type: 'number' as const },
  { value: 'total_admin_tasks', label: 'Admin Tasks', type: 'number' as const },
  { value: 'unique_checkpoints_hit', label: 'Unique Checkpoints', type: 'number' as const },
  { value: 'expected_checkpoints', label: 'Expected Checkpoints', type: 'number' as const },
  { value: 'photo_count', label: 'Photo Count', type: 'number' as const },
  { value: 'no_photo_count', label: 'No Photo Count', type: 'number' as const },
  { value: 'avg_patrol_gap_minutes', label: 'Avg Patrol Gap (min)', type: 'number' as const },
  { value: 'max_patrol_gap_minutes', label: 'Max Patrol Gap (min)', type: 'number' as const },
  { value: 'parsed_events_count', label: 'Parsed Events Count', type: 'number' as const },
  { value: 'notes_quality_score', label: 'Notes Quality Score', type: 'number' as const },
];

const BOOLEAN_FIELDS = ['dar_submitted', 'has_detailed_notes'];

const ALL_AGGREGATIONS = [
  { value: 'count', label: 'Count (rows)' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'ratio', label: 'Ratio' },
];

function getFieldType(fieldValue: string): 'boolean' | 'number' {
  const f = SUMMARY_FIELDS.find((s) => s.value === fieldValue);
  return f?.type === 'boolean' ? 'boolean' : 'number';
}

function getAggregationsForField(fieldValue: string) {
  if (getFieldType(fieldValue) === 'boolean') {
    return ALL_AGGREGATIONS.filter((a) => a.value === 'count');
  }
  return ALL_AGGREGATIONS;
}

const OPERATORS = [
  { value: '>=', label: '>= (at least)' },
  { value: '<=', label: '<= (at most)' },
  { value: '>', label: '> (more than)' },
  { value: '<', label: '< (less than)' },
  { value: '==', label: '== (equals)' },
  { value: '!=', label: '!= (not equal)' },
];

const CROSS_SCORE_AGGREGATIONS = [
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'count', label: 'Count' },
];

// ── Types ──────────────────────────────────────────────────────────

let nextCheckId = 1;
function genCheckId() {
  return `ck-${nextCheckId++}`;
}

interface CheckState {
  id: string;
  // Standard
  field: string;
  aggregation: string;
  operator: string;
  threshold: number;
  ratio_denominator: string;
  filter_field: string;
  filter_value: boolean;
  // Trend
  trend_direction: string;
  trend_threshold_pct: number;
  // Streak
  streak_weeks: number;
  // Cross-entity
  source: string;
  entity_source: string;
  inner_field: string;
  inner_aggregation: string;
  inner_operator: string;
  inner_threshold: number;
  inner_filter_field: string;
  inner_filter_value: boolean;
  count_operator: string;
  count_threshold: number;
}

interface BuilderState {
  logic: 'AND' | 'OR';
  checks: CheckState[];
}

interface ConditionBuilderProps {
  value: string;
  onChange: (json: string) => void;
  ruleType: string;
}

// ── Helpers ────────────────────────────────────────────────────────

const INPUT_CLS = 'w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const SELECT_CLS = 'w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none';
const LABEL_CLS = 'block text-xs font-medium text-slate-500 mb-1';

function makeEmptyCheck(): CheckState {
  return {
    id: genCheckId(),
    field: 'dar_submitted',
    aggregation: 'count',
    operator: '>=',
    threshold: 1,
    ratio_denominator: 'parsed_events_count',
    filter_field: '',
    filter_value: false,
    trend_direction: 'increase',
    trend_threshold_pct: 50,
    streak_weeks: 4,
    source: 'guard_scores',
    entity_source: 'vendor_sites',
    inner_field: 'dar_submitted',
    inner_aggregation: 'count',
    inner_operator: '>=',
    inner_threshold: 1,
    inner_filter_field: '',
    inner_filter_value: false,
    count_operator: '>=',
    count_threshold: 1,
  };
}

function isStandardType(ruleType: string): boolean {
  return ['threshold', 'frequency', 'absence', 'composite'].includes(ruleType);
}

// ── Parse JSON -> BuilderState ─────────────────────────────────────

function parseConditions(json: string, ruleType: string): BuilderState {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object') throw new Error('not object');

    const logic: 'AND' | 'OR' = parsed.logic === 'OR' ? 'OR' : 'AND';
    const rawChecks = Array.isArray(parsed.checks) ? parsed.checks : [];

    if (rawChecks.length === 0) {
      return { logic, checks: [] };
    }

    const checks: CheckState[] = rawChecks.map((c: Record<string, unknown>) => {
      const base = makeEmptyCheck(); // generates unique id

      if (ruleType === 'cross_entity') {
        base.source = (c.source as string) || 'guard_scores';
        if (base.source === 'daily_summary') {
          base.entity_source = (c.entity_source as string) || 'vendor_sites';
          const inner = (c.inner_check as Record<string, unknown>) || {};
          base.inner_field = (inner.field as string) || 'dar_submitted';
          base.inner_aggregation = (inner.aggregation as string) || 'count';
          base.inner_operator = (inner.operator as string) || '>=';
          base.inner_threshold = Number(inner.threshold ?? 1);
          const innerFilter = inner.filter as Record<string, boolean> | undefined;
          if (innerFilter) {
            const key = Object.keys(innerFilter)[0];
            if (key) {
              base.inner_filter_field = key;
              base.inner_filter_value = innerFilter[key];
            }
          }
          base.count_operator = (c.count_operator as string) || '>=';
          base.count_threshold = Number(c.count_threshold ?? 1);
        } else {
          base.aggregation = (c.aggregation as string) || 'avg';
          base.operator = (c.operator as string) || '<';
          base.threshold = Number(c.threshold ?? 50);
        }
      } else if (ruleType === 'trend') {
        base.field = (c.field as string) || 'total_incidents';
        base.aggregation = (c.aggregation as string) || 'sum';
        base.trend_direction = (c.trend_direction as string) || 'increase';
        base.trend_threshold_pct = Number(c.trend_threshold_pct ?? 50);
        const filter = c.filter as Record<string, boolean> | undefined;
        if (filter) {
          const key = Object.keys(filter)[0];
          if (key) { base.filter_field = key; base.filter_value = filter[key]; }
        }
      } else {
        // Standard (threshold, frequency, absence, composite) and streak
        base.field = (c.field as string) || 'dar_submitted';
        base.aggregation = (c.aggregation as string) || 'count';
        base.operator = (c.operator as string) || '>=';
        base.threshold = Number(c.threshold ?? 1);
        if (c.ratio_denominator) base.ratio_denominator = c.ratio_denominator as string;
        if (c.streak_weeks != null) base.streak_weeks = Number(c.streak_weeks);
        const filter = c.filter as Record<string, boolean> | undefined;
        if (filter) {
          const key = Object.keys(filter)[0];
          if (key) { base.filter_field = key; base.filter_value = filter[key]; }
        }
      }

      return base;
    });

    return { logic, checks };
  } catch {
    return { logic: 'AND', checks: [] };
  }
}

// ── Serialize BuilderState -> JSON string ──────────────────────────

function serializeConditions(state: BuilderState, ruleType: string): string {
  const checks = state.checks.map((c) => {
    if (ruleType === 'cross_entity') {
      if (c.source === 'daily_summary') {
        const inner: Record<string, unknown> = {
          field: c.inner_field,
          aggregation: c.inner_aggregation,
          operator: c.inner_operator,
          threshold: c.inner_threshold,
        };
        if (c.inner_filter_field) {
          inner.filter = { [c.inner_filter_field]: c.inner_filter_value };
        }
        return {
          source: 'daily_summary',
          entity_source: c.entity_source,
          inner_check: inner,
          count_operator: c.count_operator,
          count_threshold: c.count_threshold,
        };
      }
      return {
        source: c.source,
        aggregation: c.aggregation,
        operator: c.operator,
        threshold: c.threshold,
      };
    }

    if (ruleType === 'trend') {
      const check: Record<string, unknown> = {
        field: c.field,
        aggregation: c.aggregation,
        trend_direction: c.trend_direction,
        trend_threshold_pct: c.trend_threshold_pct,
      };
      if (c.filter_field) {
        check.filter = { [c.filter_field]: c.filter_value };
      }
      return check;
    }

    // Standard + streak
    const check: Record<string, unknown> = {
      field: c.field,
      aggregation: c.aggregation,
      operator: c.operator,
      threshold: c.threshold,
    };
    if (c.aggregation === 'ratio') {
      check.ratio_denominator = c.ratio_denominator;
    }
    if (c.filter_field) {
      check.filter = { [c.filter_field]: c.filter_value };
    }
    if (ruleType === 'streak') {
      check.streak_weeks = c.streak_weeks;
    }
    return check;
  });

  return JSON.stringify({ logic: state.logic, checks }, null, 2);
}

// ── Sub-components ─────────────────────────────────────────────────

function StandardCheckFields({ check, onChange }: {
  check: CheckState;
  onChange: (updates: Partial<CheckState>) => void;
}) {
  const fieldType = getFieldType(check.field);
  const validAggs = getAggregationsForField(check.field);

  const handleFieldChange = (newField: string) => {
    const updates: Partial<CheckState> = { field: newField };
    const newType = getFieldType(newField);
    // If switching to boolean, force aggregation to count & auto-set filter
    if (newType === 'boolean') {
      updates.aggregation = 'count';
      if (!check.filter_field) {
        updates.filter_field = newField;
        updates.filter_value = false;
      }
    }
    // If switching from boolean to number, and agg is still count, reset to avg
    if (newType === 'number' && fieldType === 'boolean') {
      updates.aggregation = 'avg';
    }
    onChange(updates);
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div>
        <label className={LABEL_CLS}>Field</label>
        <select value={check.field} onChange={(e) => handleFieldChange(e.target.value)} className={SELECT_CLS}>
          {SUMMARY_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>Aggregation</label>
        <select value={check.aggregation} onChange={(e) => onChange({ aggregation: e.target.value })} className={SELECT_CLS}>
          {validAggs.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        {check.aggregation === 'count' && (
          <p className="text-xs text-slate-400 mt-1">Counts shift summaries matching the filter below</p>
        )}
      </div>
      <div>
        <label className={LABEL_CLS}>Operator</label>
        <select value={check.operator} onChange={(e) => onChange({ operator: e.target.value })} className={SELECT_CLS}>
          {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>Threshold</label>
        <input type="number" step="any" value={check.threshold} onChange={(e) => onChange({ threshold: Number(e.target.value) })} className={INPUT_CLS} />
      </div>
      {check.aggregation === 'ratio' && (
        <div className="col-span-2">
          <label className={LABEL_CLS}>Denominator Field</label>
          <select value={check.ratio_denominator} onChange={(e) => onChange({ ratio_denominator: e.target.value })} className={SELECT_CLS}>
            {SUMMARY_FIELDS.filter((f) => f.type === 'number').map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">Ratio = Field / Denominator</p>
        </div>
      )}
    </div>
  );
}

function TrendCheckFields({ check, onChange }: {
  check: CheckState;
  onChange: (updates: Partial<CheckState>) => void;
}) {
  // Trend only makes sense on number fields (ratio excluded by engine)
  const numberFields = SUMMARY_FIELDS.filter((f) => f.type === 'number');
  const trendAggs = ALL_AGGREGATIONS.filter((a) => a.value !== 'ratio' && a.value !== 'count');

  // Auto-correct field if it's boolean (not valid for trend)
  useEffect(() => {
    if (getFieldType(check.field) === 'boolean') {
      onChange({ field: numberFields[0].value, aggregation: 'sum' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.field]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div>
        <label className={LABEL_CLS}>Field</label>
        <select value={check.field} onChange={(e) => onChange({ field: e.target.value })} className={SELECT_CLS}>
          {numberFields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>Aggregation</label>
        <select value={check.aggregation} onChange={(e) => onChange({ aggregation: e.target.value })} className={SELECT_CLS}>
          {trendAggs.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>Direction</label>
        <select value={check.trend_direction} onChange={(e) => onChange({ trend_direction: e.target.value })} className={SELECT_CLS}>
          <option value="increase">Increase</option>
          <option value="decrease">Decrease</option>
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>Min % Change</label>
        <input type="number" min={0} step="any" value={check.trend_threshold_pct} onChange={(e) => onChange({ trend_threshold_pct: Number(e.target.value) })} className={INPUT_CLS} />
      </div>
    </div>
  );
}

function CrossEntityCheckFields({ check, onChange }: {
  check: CheckState;
  onChange: (updates: Partial<CheckState>) => void;
}) {
  const isSummary = check.source === 'daily_summary';
  const innerFieldType = getFieldType(check.inner_field);
  const innerAggs = getAggregationsForField(check.inner_field);

  const handleInnerFieldChange = (newField: string) => {
    const updates: Partial<CheckState> = { inner_field: newField };
    const newType = getFieldType(newField);
    if (newType === 'boolean') {
      updates.inner_aggregation = 'count';
      if (!check.inner_filter_field) {
        updates.inner_filter_field = newField;
        updates.inner_filter_value = false;
      }
    }
    if (newType === 'number' && innerFieldType === 'boolean') {
      updates.inner_aggregation = 'avg';
    }
    onChange(updates);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <label className={LABEL_CLS}>Data Source</label>
          <select value={check.source} onChange={(e) => onChange({ source: e.target.value })} className={SELECT_CLS}>
            <option value="guard_scores">Guard Scores</option>
            <option value="site_scores">Site Scores</option>
            <option value="daily_summary">Daily Shift Summaries</option>
          </select>
        </div>
      </div>

      {!isSummary ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Aggregation</label>
            <select value={check.aggregation} onChange={(e) => onChange({ aggregation: e.target.value })} className={SELECT_CLS}>
              {CROSS_SCORE_AGGREGATIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Operator</label>
            <select value={check.operator} onChange={(e) => onChange({ operator: e.target.value })} className={SELECT_CLS}>
              {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Threshold</label>
            <input type="number" step="any" value={check.threshold} onChange={(e) => onChange({ threshold: Number(e.target.value) })} className={INPUT_CLS} />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLS}>Entity Source</label>
              <select value={check.entity_source} onChange={(e) => onChange({ entity_source: e.target.value })} className={SELECT_CLS}>
                <option value="vendor_sites">Vendor's Sites</option>
                <option value="vendor_guards">Vendor's Guards</option>
              </select>
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg p-3 bg-slate-50">
            <p className="text-xs font-medium text-slate-500 mb-2">Inner Check (applied to each entity)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={LABEL_CLS}>Field</label>
                <select value={check.inner_field} onChange={(e) => handleInnerFieldChange(e.target.value)} className={SELECT_CLS}>
                  {SUMMARY_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Aggregation</label>
                <select value={check.inner_aggregation} onChange={(e) => onChange({ inner_aggregation: e.target.value })} className={SELECT_CLS}>
                  {innerAggs.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Operator</label>
                <select value={check.inner_operator} onChange={(e) => onChange({ inner_operator: e.target.value })} className={SELECT_CLS}>
                  {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Threshold</label>
                <input type="number" step="any" value={check.inner_threshold} onChange={(e) => onChange({ inner_threshold: Number(e.target.value) })} className={INPUT_CLS} />
              </div>
            </div>
            {/* Inner filter */}
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Filter Field (optional)</label>
                <select value={check.inner_filter_field} onChange={(e) => onChange({ inner_filter_field: e.target.value })} className={SELECT_CLS}>
                  <option value="">No filter</option>
                  {BOOLEAN_FIELDS.map((f) => <option key={f} value={f}>{SUMMARY_FIELDS.find((s) => s.value === f)?.label}</option>)}
                </select>
              </div>
              {check.inner_filter_field && (
                <div>
                  <label className={LABEL_CLS}>Filter Value</label>
                  <select value={String(check.inner_filter_value)} onChange={(e) => onChange({ inner_filter_value: e.target.value === 'true' })} className={SELECT_CLS}>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>Count Operator</label>
              <select value={check.count_operator} onChange={(e) => onChange({ count_operator: e.target.value })} className={SELECT_CLS}>
                {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">How many entities must match the inner check</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Count Threshold</label>
              <input type="number" min={0} step={1} value={check.count_threshold} onChange={(e) => onChange({ count_threshold: Number(e.target.value) })} className={INPUT_CLS} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FilterSection({ check, onChange }: {
  check: CheckState;
  onChange: (updates: Partial<CheckState>) => void;
}) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-3">
      <div>
        <label className={LABEL_CLS}>Filter Field (optional)</label>
        <select value={check.filter_field} onChange={(e) => onChange({ filter_field: e.target.value })} className={SELECT_CLS}>
          <option value="">No filter</option>
          {BOOLEAN_FIELDS.map((f) => <option key={f} value={f}>{SUMMARY_FIELDS.find((s) => s.value === f)?.label}</option>)}
        </select>
      </div>
      {check.filter_field && (
        <div>
          <label className={LABEL_CLS}>Filter Value</label>
          <select value={String(check.filter_value)} onChange={(e) => onChange({ filter_value: e.target.value === 'true' })} className={SELECT_CLS}>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────

export default function ConditionBuilder({ value, onChange, ruleType }: ConditionBuilderProps) {
  const [state, setState] = useState<BuilderState>(() => parseConditions(value, ruleType));
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState(value);
  const [rawError, setRawError] = useState<string | null>(null);
  const prevRuleTypeRef = useRef(ruleType);
  const isExternalUpdate = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  // When ruleType changes in the parent, reset to empty
  useEffect(() => {
    if (prevRuleTypeRef.current !== ruleType) {
      prevRuleTypeRef.current = ruleType;
      const newState: BuilderState = { logic: 'AND', checks: [] };
      setState(newState);
      const json = serializeConditions(newState, ruleType);
      setRawJson(json);
      isExternalUpdate.current = true;
      onChange(json);
    }
  }, [ruleType, onChange]);

  // When external value changes (e.g., loading existing rule), re-parse
  useEffect(() => {
    if (isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }
    const currentJson = serializeConditions(stateRef.current, ruleType);
    if (value !== currentJson) {
      const parsed = parseConditions(value, ruleType);
      setState(parsed);
      setRawJson(value);
    }
    // Only run when value changes externally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emitChange = useCallback((newState: BuilderState) => {
    setState(newState);
    const json = serializeConditions(newState, ruleType);
    setRawJson(json);
    isExternalUpdate.current = true;
    onChange(json);
  }, [ruleType, onChange]);

  const updateCheck = useCallback((index: number, updates: Partial<CheckState>) => {
    const prev = stateRef.current;
    emitChange({
      ...prev,
      checks: prev.checks.map((c, i) => i === index ? { ...c, ...updates } : c),
    });
  }, [emitChange]);

  const addCheck = useCallback(() => {
    const prev = stateRef.current;
    emitChange({ ...prev, checks: [...prev.checks, makeEmptyCheck()] });
  }, [emitChange]);

  const removeCheck = useCallback((index: number) => {
    const prev = stateRef.current;
    emitChange({ ...prev, checks: prev.checks.filter((_, i) => i !== index) });
  }, [emitChange]);

  const setLogic = useCallback((logic: 'AND' | 'OR') => {
    const prev = stateRef.current;
    emitChange({ ...prev, logic });
  }, [emitChange]);

  const handleToggleRaw = () => {
    if (rawMode) {
      // Switching back to visual — parse the raw JSON
      try {
        JSON.parse(rawJson);
        setRawError(null);
        const parsed = parseConditions(rawJson, ruleType);
        setState(parsed);
        isExternalUpdate.current = true;
        onChange(rawJson);
        setRawMode(false);
      } catch {
        setRawError('Invalid JSON — fix errors before switching back to visual mode');
      }
    } else {
      setRawJson(serializeConditions(state, ruleType));
      setRawError(null);
      setRawMode(true);
    }
  };

  const handleRawChange = (json: string) => {
    setRawJson(json);
    setRawError(null);
    try {
      JSON.parse(json);
      isExternalUpdate.current = true;
      onChange(json);
    } catch {
      // Don't emit invalid JSON
    }
  };

  return (
    <div className="space-y-4">
      {/* Logic Toggle */}
      {state.checks.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Combine checks with:</span>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            {(['AND', 'OR'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLogic(l)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  state.logic === l ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!rawMode && state.checks.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center">
          <p className="text-sm text-slate-400 mb-2">No conditions defined yet.</p>
          <button
            type="button"
            onClick={addCheck}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            + Add your first check
          </button>
        </div>
      )}

      {/* Check Rows */}
      {!rawMode && state.checks.map((check, i) => (
        <div key={check.id} className="border border-slate-200 rounded-xl p-4 relative">
          <button
            type="button"
            onClick={() => removeCheck(i)}
            className="absolute top-3 right-3 text-slate-400 hover:text-red-500 transition-colors"
            title="Remove check"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {state.checks.length > 1 && (
            <p className="text-xs font-medium text-slate-400 mb-3">Check {i + 1}</p>
          )}

          {/* Standard: threshold, frequency, absence, composite */}
          {isStandardType(ruleType) && (
            <>
              <StandardCheckFields check={check} onChange={(u) => updateCheck(i, u)} />
              <FilterSection check={check} onChange={(u) => updateCheck(i, u)} />
            </>
          )}

          {/* Trend */}
          {ruleType === 'trend' && (
            <>
              <TrendCheckFields check={check} onChange={(u) => updateCheck(i, u)} />
              <FilterSection check={check} onChange={(u) => updateCheck(i, u)} />
            </>
          )}

          {/* Streak */}
          {ruleType === 'streak' && (
            <>
              <StandardCheckFields check={check} onChange={(u) => updateCheck(i, u)} />
              <div className="mt-3 max-w-xs">
                <label className={LABEL_CLS}>Consecutive Weeks</label>
                <input
                  type="number"
                  min={4}
                  value={check.streak_weeks}
                  onChange={(e) => updateCheck(i, { streak_weeks: Math.max(4, Number(e.target.value)) })}
                  className={INPUT_CLS}
                />
                {check.streak_weeks < 4 && (
                  <p className="text-xs text-amber-600 mt-1">Minimum 4 weeks enforced by engine</p>
                )}
              </div>
              <FilterSection check={check} onChange={(u) => updateCheck(i, u)} />
            </>
          )}

          {/* Cross-entity */}
          {ruleType === 'cross_entity' && (
            <CrossEntityCheckFields check={check} onChange={(u) => updateCheck(i, u)} />
          )}
        </div>
      ))}

      {/* Add Check Button */}
      {!rawMode && state.checks.length > 0 && (
        <button
          type="button"
          onClick={addCheck}
          className="flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Check
        </button>
      )}

      {/* Raw JSON Toggle */}
      <div className="border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={handleToggleRaw}
          className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          <svg className={`w-3.5 h-3.5 transition-transform ${rawMode ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          {rawMode ? 'Back to Visual Builder' : 'Advanced: Edit Raw JSON'}
        </button>

        {rawMode && (
          <div className="mt-2">
            {rawError && (
              <p className="text-xs text-red-600 mb-2">{rawError}</p>
            )}
            <textarea
              value={rawJson}
              onChange={(e) => handleRawChange(e.target.value)}
              rows={10}
              className={`w-full font-mono text-xs rounded-xl border-2 px-3 py-2 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ${
                rawError ? 'border-red-300' : 'border-slate-200'
              }`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
